
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBackToDashboard } from "../../utils/navigationUtils";
import {
  User,
  FileText,
  Upload,
  X,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Phone,
  Mail,
  CreditCard,
  Stethoscope,
  Building,
  Users,
  ArrowLeft,
  ArrowRight,
  Save,
  Eye,
  AlertTriangle,
  RefreshCw,
  Clock,
  History,
  Info,
  Calendar
} from "lucide-react";
import { createPatientApplication } from "../../services/applicationService";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../services/firebaseConfig";
import { useAuth } from "../../context/AuthContext";

const NewPatientApplication = ({ onBack }) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const goBackToDashboard = useBackToDashboard();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const [caseId, setCaseId] = useState("");

  const hospitalId = userData?.hospitalId;
  const isSuperAdmin = userData?.role === 'superadmin'; // Assuming role is available in userData

  const [formData, setFormData] = useState({
    // Patient Information
    patientName: "",
    patientAge: "",
    patientDob: "", // Date of birth for patient
    patientGender: "",
    patientAddress: "",
    patientPhone: "",
    patientEmail: "",

    // Patient KYC
    patientAadhar: "",
    patientPan: "",
    patientAddressProof: "",

    // Policy Holder Info
  isSelf: false, // Default to unchecked; user can opt-in if patient is the policy holder
    policyHolderName: "",
    policyHolderAge: "",
    policyHolderGender: "",
    policyHolderAddress: "",
    policyHolderPhone: "",
    policyHolderEmail: "",
    policyHolderDob: "", // New: DOB (no future dates)
    policyHolderAlternatePhone: "", // New: Alternate mobile number
    relationshipWithPatient: "Self",

    // Policy Holder KYC
    policyHolderAadhar: "",
    policyHolderPan: "",
    policyHolderAddressProof: "",

    // Nominee Information
    nomineeName: "",
    relationshipWithNominee: "",
    nomineeContact: "",

    // Policy Information (moved before diagnosis)
    policyType: "",
    corporateName: "",
    policyNumber: "",
    policyStartDate: "",
    policyEndDate: "",
    insuranceCompany: "",
    tpaName: "",

    // Employment Information
    employmentStatus: "",
    workingCompanyName: "",
    monthlyIncome: "",
    designation: "",
    natureOfWork: "",

    // Medical History
    hasMedicalHistory: false,
    medicalHistory: "",
    medicalDiagnosis: "",
    medicalDuration: "",
    surgicalHistory: "",
    surgicalDiagnosis: "",
    surgicalDuration: "",
    surgicalProcedure: "",

    // Diagnosis Details
    treatmentType: "medical",
    doctorName: "",
    doctorDesignation: "",
    admissionDate: "",
    dischargeDate: "",
    estimatedCost: "",
    // actualCost removed as requested

    // Additional Information moved to diagnosis section
    remarks: "",
    priority: "normal",
    emergency: false,
    treatmentPlanned: "planned", // emergency or planned
    // Financing / Disbursal Preference (new)
    paymentPreference: "", // 'pdc' | 'loan'
  });

  const [treatments, setTreatments] = useState([]);
  const [newTreatment, setNewTreatment] = useState({
    diagnosisName: "",
    treatmentType: "",
    procedureName: "",
    description: "",
  });

  // Auto-save functionality
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('newPatientApplication');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        const confirmContinue = window.confirm(
          'Found an incomplete application. Would you like to continue from where you left off or start a new application?'
        );

        if (confirmContinue) {
          setFormData(parsedData.formData || formData);
          setTreatments(parsedData.treatments || []);
          setCurrentStep(parsedData.currentStep || 1);
        } else {
          localStorage.removeItem('newPatientApplication');
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        localStorage.removeItem('newPatientApplication');
      }
    }
  }, []);

  // Auto-save data whenever formData or treatments change
  useEffect(() => {
    if (hasUnsavedChanges) {
      // Clean formData to avoid undefined values in localStorage
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [
          key,
          value === undefined ? "" : value
        ])
      );

      const saveData = {
        formData: cleanedFormData,
        treatments,
        currentStep,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('newPatientApplication', JSON.stringify(saveData));
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    }
  }, [formData, treatments, currentStep, hasUnsavedChanges]);

  // Trigger auto-save when data changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [formData, treatments]);

  // Fetch lead generators on component mount
  useEffect(() => {
    fetchLeadGenerators();
  }, []);

  // Function to fetch the lead generator who onboarded this hospital
  const fetchLeadGenerators = async () => {
    setLoadingLeadGenerators(true);
    try {
      // Get hospital details to find the lead generator who onboarded them
      const hospitalDocRef = doc(db, "hospitalApplications", hospitalId);
      const hospitalDoc = await getDoc(hospitalDocRef);

      if (!hospitalDoc.exists()) {
        console.error("Hospital not found");
        setLeadGenerators([]);
        return;
      }
      console.log("Hospital found:", hospitalDoc.id);
      const hospitalData = hospitalDoc.data();
      console.log("Hospital data:", hospitalData);

      // Check if hospital has lead generator referral information
      // Try multiple possible referral data structures for backwards compatibility
      const referralData = hospitalData.referral || hospitalData.referralData;
      if (referralData && referralData.leadGeneratorId) {
        // Get the specific lead generator who referred this hospital
        const leadGeneratorId = referralData.leadGeneratorId;

        // Get lead generator details from leadGeneratorApplications collection
        const leadGenDocRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
        const leadGenDoc = await getDoc(leadGenDocRef);

        if (leadGenDoc.exists()) {
          const leadGenData = leadGenDoc.data();

          // Only show this lead generator if they are approved and active
          if (leadGenData.status === 'approved' && leadGenData.isActive) {
            const assignedLeadGen = {
              id: leadGenDoc.id,
              entityName: leadGenData.entityName || "",
              city: leadGenData.city || "",
              state: leadGenData.state || "",
              servicesRequired: leadGenData.servicesRequired || [],
              cinNumber: leadGenData.cinNumber || "",
              leadGeneratorId: leadGenData.leadGeneratorId || leadGenDoc.id, // Fallback to document ID if leadGeneratorId is undefined
            };

            setLeadGenerators([assignedLeadGen]);
            // Auto-select the assigned lead generator
            setSelectedLeadGenerator(assignedLeadGen);
          } else {
            setLeadGenerators([]);
            console.warn("Lead generator is not approved or not active");
          }
        } else {
          setLeadGenerators([]);
          console.error("Lead generator not found");
        }
      } else {
        // If no referral data, no lead generators will be shown - leads won't be assigned to anyone
        setLeadGenerators([]);
        console.log("No lead generator referral data found for this hospital. No leads will be assigned.");
      }
    } catch (error) {
      console.error("Error fetching lead generators:", error);
      setLeadGenerators([]);
    } finally {
      setLoadingLeadGenerators(false);
    }
  };

  const [documents, setDocuments] = useState({
    // Patient KYC Documents
    patientAadharFrontDoc: null,
    patientAadharBackDoc: null,
    patientPanDoc: null,
    patientAddressProofDoc: null,

    // Policy Holder KYC Documents (legacy optional fields retained if needed elsewhere)
    policyHolderAadharDoc: null,
    policyHolderPanDoc: null,
    policyHolderAddressProofDoc: null,

    // Treatment Documents
    treatmentDocs: {},

    // Medical History Documents (multiple uploads)
    medicalHistoryDocs: [],

    // Medical Documents (new mandatory section, multiple uploads)
    medicalDocs: [],

    // Policy Copy Documents (multiple uploads)
    patientPolicyCopyDocs: [],
    policyHolderPolicyCopyDocs: [],
    // Investigation Reports (optional, multiple uploads)
    investigationReports: [],
  });

  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // Preview state for documents (image/PDF)
  const [preview, setPreview] = useState({ open: false, url: '', type: '', name: '' });

  const openPreview = (file) => {
    if (!file) return;
    try {
      const url = URL.createObjectURL(file);
      setPreview({ open: true, url, type: file.type || '', name: file.name || 'Document preview' });
    } catch (e) {
      console.error('Failed to preview file', e);
    }
  };

  const closePreview = () => {
    try {
      if (preview.url) URL.revokeObjectURL(preview.url);
    } catch { }
    setPreview({ open: false, url: '', type: '', name: '' });
  };

  const [showAdmissionDateOverride, setShowAdmissionDateOverride] = useState(false);
  // Drag-and-drop visual state per drop zone key
  const [dragOver, setDragOver] = useState({});

  // Drag-and-drop helpers
  const handleDragOverGeneric = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver((prev) => ({ ...prev, [key]: true }));
  };

  const handleDragLeaveGeneric = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver((prev) => ({ ...prev, [key]: false }));
  };

  const handleDropSingle = (event, docKey) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver((prev) => ({ ...prev, [docKey]: false }));
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    handleFileUpload(docKey, files[0]);
  };

  const handleDropMultiple = (event, key, multiUploadHandler) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver((prev) => ({ ...prev, [key]: false }));
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    multiUploadHandler(files);
  };

  // Lead Generator state
  const [leadGenerators, setLeadGenerators] = useState([]);
  const [selectedLeadGenerator, setSelectedLeadGenerator] = useState(null);
  const [showLeadSelection, setShowLeadSelection] = useState(false);
  const [loadingLeadGenerators, setLoadingLeadGenerators] = useState(false);

  // Compute missing required documents for the Upload step
  const getMissingRequiredDocs = () => {
    const missing = [];

    // Patient KYC
    if (!documents.patientAadharFrontDoc) missing.push("Patient Aadhar - Front");
    if (!documents.patientAadharBackDoc) missing.push("Patient Aadhar - Back");
    // PAN: required when policy holder is self
    if (formData.isSelf && !documents.patientPanDoc) missing.push("Patient PAN Card");
    // Address Proof optional — do not include in missing list

    // Policy docs
    if (formData.isSelf) {
      if (!documents.patientChequeOrStatementDoc) missing.push("Cancelled Cheque / Bank Statement");
      if (!documents.patientPolicyCopyDocs || documents.patientPolicyCopyDocs.length === 0) missing.push("Policy Copy");
    } else {
      if (!documents.policyHolderAadharFrontDoc) missing.push("Policyholder Aadhar - Front");
      if (!documents.policyHolderAadharBackDoc) missing.push("Policyholder Aadhar - Back");
      if (!documents.policyHolderPanDoc) missing.push("Policyholder PAN Card");
      if (!documents.policyHolderChequeOrStatementDoc) missing.push("Policyholder Cheque / Statement");
      if (!documents.policyHolderPolicyCopyDocs || documents.policyHolderPolicyCopyDocs.length === 0) missing.push("Policyholder Policy Copy");
    }

    // Medical docs (mandatory at least one)
    if (!documents.medicalDocs || documents.medicalDocs.length === 0) {
      missing.push("At least one Medical Document");
    }

    return missing;
  };

  const steps = [
    {
      id: 1,
      title: "Patient Information",
      icon: User,
      description: "Basic patient details",
    },
    {
      id: 2,
      title: "Policy Holder Info",
      icon: Users,
      description: "Policy holder, nominee, and employment",
    },
    {
      id: 3,
      title: "KYC Documents",
      icon: FileText,
      description: "Patient verification documents",
    },
    {
      id: 4,
      title: "Medical History",
      icon: History,
      description: "Patient medical background",
    },
    {
      id: 5,
      title: "Policy Information",
      icon: CreditCard,
      description: "Insurance policy details",
    },
    {
      id: 6,
      title: "Diagnosis Details",
      icon: Stethoscope,
      description: "Medical diagnosis and treatment",
    },
    {
      id: 7,
      title: "Upload Documents",
      icon: Upload,
      description: "Upload all required files",
    },
    {
      id: 8,
      title: "Review & Submit",
      icon: CheckCircle,
      description: "Final review and submission",
    },
  ];

  const genderOptions = ["Male", "Female", "Other"];
  const treatmentTypes = ["medical", "surgical"];
  const relationshipOptions = [
    "Self",
    "Spouse",
    "Father",
    "Mother",
    "Son",
    "Daughter",
    "Brother",
    "Sister",
    "Father-in-law",
    "Mother-in-law",
    "Other",
  ];
  const insuranceCompanies = [
    "Bajaj Allianz General Insurance Co. Ltd",
    "Cholamandalam MS General Insurance Co. Ltd",
    "Navi General Insurance Ltd.",
    "Future Generali India Insurance Co. Ltd.",
    "HDFC ERGO General Insurance Co. Ltd",
    "IFFCO-TOKIO General Insurance Co. Ltd.",
    "ICICI Lombard General Insurance Co. Ltd",
    "Kotak Mahindra General insurance co. Ltd.",
    "Liberty General Insurance Co. Ltd.",
    "Magma HDI General Insurance Co. Ltd.",
    "National Insurance Co. Ltd.",
    "Raheja QBE General Insurance Co. Ltd.",
    "Reliance General Insurance Co. Ltd.",
    "Royal Sundaram General Insurance Co. Ltd",
    "SBI General Insurance Co. Ltd.",
    "Shriram General Insurance Co. Ltd.",
    "Tata-AIG General Insurance Co. Ltd.",
    "The New India Assurance Co. Ltd.",
    "The Oriental Insurance Co. Ltd.",
    "Universal Sompo General Insurance Co. Ltd.",
    "United India Insurance Co. Ltd.",
    "Acko General Insurance Ltd.",
    "Edelweiss General Insurance Company Limited",
    "Go Digit General Insurance Limited",
    "Aditya Birla Health insurance Co Ltd.",
    "Manipal Cigna Health Insurance Company Limited",
    "Niva bupa health insurance company limited",
    "Care Health Insurance Ltd",
    "Star Health & Allied Insurance Co. Ltd",
    "Digit Insurance"
  ];

  const tpaCompanies = [
    "In-House",
    "Medi Assist Insurance TPA Private Limited",
    "MDIndia Health Insurance TPA Private Limited",
    "Paramount Health Services & Insurance TPA Private Limited",
    "Heritage Health Insurance TPA Private Limited",
    "Family Health Plan Insurance TPA Limited",
    "Raksha Health Insurance TPA Private Limited",
    "Vidal Health Insurance TPA Private Limited",
    "Volo Health Insurance TPA Pvt. Ltd ( Formerly East West Assist Insurance TPA Private Limited)",
    "Medsave Health Insurance TPA Limited",
    "Genins India Insurance TPA Limited",
    "Health India Insurance TPA Services Private Limited",
    "Good Health Insurance TPA Limited",
    "Park Mediclaim Insurance TPA Private Limited",
    "Safeway Insurance TPA Private Limited",
    "Ericson Insurance TPA Private Limited",
    "Health Insurance TPA of India Limited",
    "Vision Digital Insurance TPA Private Limited",
    "Link-K Insurance TPA Private Limited",
    "AKNA Health Insurance TPA Private Limited",
  ];

  // Helper function to check if admission date is within 3 days
  const isAdmissionDateValid = (date) => {
    if (!date) return false;
    const admissionDate = new Date(date);
    const currentDate = new Date();
    const threeDaysAgo = new Date(currentDate.getTime() - (3 * 24 * 60 * 60 * 1000));
    return admissionDate >= threeDaysAgo;
  };

  // Helper function to check if admission date is within policy period
  const isAdmissionWithinPolicyPeriod = (admissionDate, policyStartDate, policyEndDate) => {
    if (!admissionDate || !policyStartDate || !policyEndDate) return false;
    const admission = new Date(admissionDate);
    const policyStart = new Date(policyStartDate);
    const policyEnd = new Date(policyEndDate);
    return admission >= policyStart && admission <= policyEnd;
  };

  // Helper function to validate policy duration (should be whole years)
  const validatePolicyDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return { valid: false, message: "" };

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) {
      return { valid: false, message: "End date must be after start date" };
    }

    // Calculate the difference in days
    const diffTime = end.getTime() - start.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);

    // Check if it's exactly whole years (365 days per year)
    const years = Math.round(diffDays / 365);
    const expectedDays = years * 365;

    if (Math.abs(diffDays - expectedDays) > 1) {
      return {
        valid: false,
        message: "Policy duration must be a whole number of years (e.g., 1, 2, 3 years)"
      };
    }

    return {
      valid: true,
      message: `Policy duration is ${years} year${years === 1 ? "" : "s"}`,
      years: years
    };
  };

  // Helper function to check policy expiry status
  const checkPolicyExpiry = (endDate) => {
    if (!endDate) return { status: "unknown", message: "" };

    const end = new Date(endDate);
    const today = new Date();
    const fifteenDaysFromNow = new Date(today.getTime() + (15 * 24 * 60 * 60 * 1000));

    if (end < today) {
      return {
        status: "expired",
        message: "Policy has expired. Please renew or provide renewal status."
      };
    } else if (end <= fifteenDaysFromNow) {
      return {
        status: "expiring",
        message: "Policy is expiring within 15 days. Please check renewal status."
      };
    }

    return { status: "active", message: "" };
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();

    // Check if birthday hasn't occurred this year yet
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return Math.max(0, age); // Ensure age is not negative
  };

  // Helper function to parse various date formats
  const parseDateInput = (input) => {
    // Remove any whitespace
    const cleanInput = input.trim();

    // Check if it's just a number (age)
    if (/^\d+$/.test(cleanInput)) {
      return null; // Return null if it's just a number
    }

    // Parse common date formats
    const formats = [
      /(\d{1,2})[/-](\d{1,2})[/-](\d{4})/, // DD-MM-YYYY or DD/MM/YYYY
      /(\d{4})[/-](\d{1,2})[/-](\d{1,2})/, // YYYY-MM-DD or YYYY/MM/DD
      /(\d{1,2})[/-](\d{1,2})[/-](\d{2})/, // DD-MM-YY or DD/MM/YY
    ];

    for (const regex of formats) {
      const match = cleanInput.match(regex);
      if (match) {
        let day, month, year;

        if (regex.source.includes('\\d{4}')) {
          // Format has 4-digit year
          if (match[1].length === 4) {
            // YYYY-MM-DD format
            [, year, month, day] = match;
          } else {
            // DD-MM-YYYY format
            [, day, month, year] = match;
          }
        } else {
          // Format has 2-digit year
          [, day, month, year] = match;
          // Convert 2-digit year to 4-digit (assuming years 00-50 are 2000-2050, 51-99 are 1951-1999)
          year = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
        }

        // Create date object
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        
        // Validate date
        if (
          date.getFullYear() == year &&
          date.getMonth() + 1 == month &&
          date.getDate() == day &&
          date <= new Date() // Ensure it's not a future date
        ) {
          return date.toISOString().split('T')[0]; // Return in YYYY-MM-DD format
        }
      }
    }

    return null; // No valid date found
  };

  // Handler for age field blur - process date input when user leaves the field
  const handleAgeBlur = (name, value) => {
    if (!value || !value.trim()) return;
    
    // Try to parse date format
    const parsedDate = parseDateInput(value.trim());
    if (parsedDate) {
      // Valid date entered, calculate and set age
      const calculatedAge = calculateAge(parsedDate);
      
      setFormData((prev) => {
        const updated = { ...prev };
        updated[name] = calculatedAge.toString();
        
        // Also update the corresponding DOB field if it exists
        if (name === 'patientAge' && prev.patientDob !== undefined) {
          updated.patientDob = parsedDate;
        } else if (name === 'policyHolderAge') {
          updated.policyHolderDob = parsedDate;
        }
        
        return updated;
      });
    } else {
      // Check if it's just a number
      if (/^\d+$/.test(value.trim())) {
        // It's just a number, keep it
        setFormData((prev) => ({
          ...prev,
          [name]: value.trim(),
        }));
      }
      // If neither valid date nor valid number, keep the raw input
      // Let validation catch it later
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let processedValue = value;

    // Guard for Policy Holder DOB: clamp to today (no future dates)
    if (name === 'policyHolderDob') {
      try {
        const today = new Date();
        const entered = new Date(value);
        if (entered > today) {
          // clamp to today in yyyy-mm-dd
          const todayStr = new Date().toISOString().split('T')[0];
          processedValue = todayStr;
        }
      } catch {}
    }

    // Special handling for PAN fields
    if (name.includes('Pan') && type !== "checkbox") {
      // Convert to uppercase
      processedValue = value.toUpperCase();

      // Allow only alphanumeric characters
      processedValue = processedValue.replace(/[^A-Z0-9]/g, '');
    }

    // Special handling for policy number field
    if (name === 'policyNumber' && type !== "checkbox") {
      // Convert to uppercase
      processedValue = value.toUpperCase();
    }

    // Smart age input handling - for patient and policy holder age
    // Let the user type freely, we'll parse on blur
    // For now, just store the raw input
    if ((name === 'patientAge' || name === 'policyHolderAge') && type !== "checkbox") {
      // Just store the raw input value, process on blur to avoid interrupting typing
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));

    // Auto-populate policy holder data if self
    if (name === "isSelf") {
      if (checked) {
        setFormData((prev) => ({
          ...prev,
          policyHolderName: prev.patientName,
          policyHolderAge: prev.patientAge,
          policyHolderGender: prev.patientGender,
          policyHolderAddress: prev.patientAddress,
          policyHolderPhone: prev.patientPhone,
          policyHolderEmail: prev.patientEmail,
          policyHolderAadhar: prev.patientAadhar,
          policyHolderPan: prev.patientPan,
          policyHolderAddressProof: prev.patientAddressProof,
          relationshipWithPatient: "Self",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          policyHolderName: "",
          policyHolderAge: "",
          policyHolderGender: "",
          policyHolderAddress: "",
          policyHolderPhone: "",
          policyHolderEmail: "",
          policyHolderAadhar: "",
          policyHolderPan: "",
          policyHolderAddressProof: "",
          relationshipWithPatient: "",
        }));
      }
    }

    // Clear medical history fields when checkbox is unchecked
    if (name === "hasMedicalHistory") {
      if (!checked) {
        setFormData((prev) => ({
          ...prev,
          medicalHistory: "",
          medicalDiagnosis: "",
          medicalDuration: "",
          surgicalHistory: "",
          surgicalDiagnosis: "",
          surgicalDuration: "",
          surgicalProcedure: "",
        }));
        // Clear medical history documents
        setDocuments((prev) => ({
          ...prev,
          medicalHistoryDocs: [],
        }));
      }
    }

    // Clear employment fields when policy type changes
      // NOTE: Removed previous logic that cleared employment fields on policy type change.
      // Policy type does not inherently invalidate employment info; preserving user input avoids accidental data loss.
      if (name === "policyType") {
        // Do not clear employment fields
      }

    // Clear or retain fields when employment status changes
    if (name === "employmentStatus") {
      setFormData((prev) => {
        // Preserve previous values in a lightweight cache to restore if user toggles back
        const cache = prev.__employmentCache || {
          employed: {
            workingCompanyName: prev.workingCompanyName || "",
            designation: prev.designation || ""
          },
          selfEmployed: {
            natureOfWork: prev.natureOfWork || ""
          },
          business: {
            natureOfWork: prev.natureOfWork || ""
          }
        };

        if (prev.employmentStatus === "employed") {
          cache.employed.workingCompanyName = prev.workingCompanyName || "";
          cache.employed.designation = prev.designation || "";
        }
        if (prev.employmentStatus === "self-employed") {
          cache.selfEmployed.natureOfWork = prev.natureOfWork || "";
        }
        if (prev.employmentStatus === "business") {
          cache.business.natureOfWork = prev.natureOfWork || "";
        }

        return {
          ...prev,
          employmentStatus: value,
          __employmentCache: cache,
          // Only blank out fields that are not relevant to the newly selected status;
          // keep cached values for when user toggles back.
          workingCompanyName: value === "employed" ? (cache.employed.workingCompanyName || prev.workingCompanyName || "") : prev.workingCompanyName,
          designation: value === "employed" ? (cache.employed.designation || prev.designation || "") : prev.designation,
          natureOfWork: (value === "self-employed" || value === "business")
            ? (prev.natureOfWork || cache.selfEmployed.natureOfWork || cache.business.natureOfWork || "")
            : prev.natureOfWork,
        };
      });
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileUpload = (docType, file) => {
    if (!file) return;
    // Allow larger files (up to 100MB) for cheque/bank statement uploads
    const isChequeOrStatement =
      docType === "policyHolderChequeOrStatementDoc" ||
      docType === "patientChequeOrStatementDoc";
    const maxSize = isChequeOrStatement ? 100 * 1024 * 1024 : 5 * 1024 * 1024; // 100MB for cheque/statement, 5MB otherwise
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [docType]: `File size should be less than ${isChequeOrStatement ? "100MB" : "5MB"}`,
      }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [docType]: "Please upload PDF, JPEG, or PNG files only",
      }));
      return;
    }

    setDocuments((prev) => ({ ...prev, [docType]: file }));
    setErrors((prev) => ({ ...prev, [docType]: "" }));

    // Simulate upload progress
    setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const progress = (prev[docType] || 0) + 10;
        if (progress >= 100) {
          clearInterval(interval);
          return { ...prev, [docType]: 100 };
        }
        return { ...prev, [docType]: progress };
      });
    }, 100);
  };

  // Handle multiple medical history document uploads
  const handleMedicalHistoryDocUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 100 * 1024 * 1024; // 100MB per file
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const validFiles = [];
    const fileErrors = [];

    Array.from(files).forEach((file, index) => {
      if (file.size > maxSize) {
        fileErrors.push(`File ${file.name}: Size should be less than 100MB`);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors((prev) => ({
        ...prev,
        medicalHistoryDocs: fileErrors.join(', '),
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      medicalHistoryDocs: [...prev.medicalHistoryDocs, ...validFiles],
    }));
    setErrors((prev) => ({ ...prev, medicalHistoryDocs: "" }));
  };

  // Handle multiple medical documents uploads (mandatory min 1)
  const handleMedicalDocsUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 100 * 1024 * 1024; // 100MB per file
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const validFiles = [];
    const fileErrors = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        fileErrors.push(`File ${file.name}: Size should be less than 100MB`);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors((prev) => ({
        ...prev,
        medicalDocs: fileErrors.join(', '),
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      medicalDocs: [...prev.medicalDocs, ...validFiles],
    }));
    setErrors((prev) => ({ ...prev, medicalDocs: "" }));
  };

  const removeMedicalDoc = (index) => {
    setDocuments((prev) => ({
      ...prev,
      medicalDocs: prev.medicalDocs.filter((_, i) => i !== index),
    }));
  };

  // Handle multiple Investigation Reports uploads (optional)
  const handleInvestigationReportsUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 100 * 1024 * 1024; // 100MB per file
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const validFiles = [];
    const fileErrors = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        fileErrors.push(`File ${file.name}: Size should be less than 100MB`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
        return;
      }
      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors((prev) => ({
        ...prev,
        investigationReports: fileErrors.join(', '),
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      investigationReports: [...prev.investigationReports, ...validFiles],
    }));
    setErrors((prev) => ({ ...prev, investigationReports: "" }));
  };

  const removeInvestigationReport = (index) => {
    setDocuments((prev) => ({
      ...prev,
      investigationReports: prev.investigationReports.filter((_, i) => i !== index),
    }));
  };

  const removeMedicalHistoryDoc = (index) => {
    setDocuments((prev) => ({
      ...prev,
      medicalHistoryDocs: prev.medicalHistoryDocs.filter((_, i) => i !== index),
    }));
  };

  // Multiple upload handlers for Policy Copy (patient/self)
  const handlePatientPolicyCopyUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 5 * 1024 * 1024; // 5MB per file (kept as-is)
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const validFiles = [];
    const fileErrors = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        fileErrors.push(`File ${file.name}: Size should be less than 5MB`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(
          `File ${file.name}: Please upload PDF, JPEG, or PNG files only`
        );
        return;
      }
      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors((prev) => ({
        ...prev,
        patientPolicyCopyDocs: fileErrors.join(", "),
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      patientPolicyCopyDocs: [
        ...(prev.patientPolicyCopyDocs || []),
        ...validFiles,
      ],
    }));
    setErrors((prev) => ({ ...prev, patientPolicyCopyDocs: "" }));
  };

  // Multiple upload handlers for Policy Copy (policyholder)
  const handlePolicyHolderPolicyCopyUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 5 * 1024 * 1024; // 5MB per file (kept as-is)
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const validFiles = [];
    const fileErrors = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        fileErrors.push(`File ${file.name}: Size should be less than 5MB`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(
          `File ${file.name}: Please upload PDF, JPEG, or PNG files only`
        );
        return;
      }
      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors((prev) => ({
        ...prev,
        policyHolderPolicyCopyDocs: fileErrors.join(", "),
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      policyHolderPolicyCopyDocs: [
        ...(prev.policyHolderPolicyCopyDocs || []),
        ...validFiles,
      ],
    }));
    setErrors((prev) => ({ ...prev, policyHolderPolicyCopyDocs: "" }));
  };

  const removePatientPolicyCopyDoc = (index) => {
    setDocuments((prev) => ({
      ...prev,
      patientPolicyCopyDocs: (prev.patientPolicyCopyDocs || []).filter(
        (_, i) => i !== index
      ),
    }));
  };

  const removePolicyHolderPolicyCopyDoc = (index) => {
    setDocuments((prev) => ({
      ...prev,
      policyHolderPolicyCopyDocs: (
        prev.policyHolderPolicyCopyDocs || []
      ).filter((_, i) => i !== index),
    }));
  };

  const removeFile = (docType) => {
    setDocuments((prev) => ({ ...prev, [docType]: null }));
    setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));
  };

  const addTreatment = () => {
    if (!newTreatment.diagnosisName.trim()) {
      setErrors((prev) => ({
        ...prev,
        diagnosisName: "Diagnosis name is required",
      }));
      return;
    }

    const treatment = {
      id: Date.now(),
      ...newTreatment,
    };

    setTreatments((prev) => [...prev, treatment]);
    setNewTreatment({
      diagnosisName: "",
      treatmentType: "",
      procedureName: "",
      description: "",
    });
    setErrors((prev) => ({ ...prev, diagnosisName: "" }));
  };

  const removeTreatment = (treatmentId) => {
    setTreatments((prev) => prev.filter((t) => t.id !== treatmentId));
    // Also remove associated documents
    setDocuments((prev) => {
      const newDocs = { ...prev };
      delete newDocs.treatmentDocs[treatmentId];
      return newDocs;
    });
  };

  const handleTreatmentDocUpload = (treatmentId, file) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [`treatmentDoc_${treatmentId}`]: "File size should be less than 5MB",
      }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [`treatmentDoc_${treatmentId}`]:
          "Please upload PDF, JPEG, or PNG files only",
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      treatmentDocs: {
        ...prev.treatmentDocs,
        [treatmentId]: file,
      },
    }));

    setErrors((prev) => ({ ...prev, [`treatmentDoc_${treatmentId}`]: "" }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Patient Information
        if (!formData.patientName.trim())
          newErrors.patientName = "Patient name is required";
        if (!formData.patientAge)
          newErrors.patientAge = "Patient age is required";
        else if (
          isNaN(parseInt(formData.patientAge)) ||
          parseInt(formData.patientAge) < 0 ||
          parseInt(formData.patientAge) > 120
        )
          newErrors.patientAge = "Invalid age";
        if (!formData.patientGender)
          newErrors.patientGender = "Patient gender is required";
        if (!formData.patientAddress.trim())
          newErrors.patientAddress = "Patient address is required";
        if (!formData.patientPhone.trim())
          newErrors.patientPhone = "Patient phone is required";
        else if (
          !/^[6-9]\d{9}$/.test(formData.patientPhone.replace(/\s+/g, ""))
        )
          newErrors.patientPhone = "Invalid phone number";
        if (
          formData.patientEmail &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patientEmail)
        )
          newErrors.patientEmail = "Invalid email format";
        break;
      case 2: // Policy Holder Info + Employment
        // Policy holder core (for non-self)
        if (!formData.isSelf) {
          if (!formData.policyHolderName.trim())
            newErrors.policyHolderName = "Policy holder name is required";
          if (!formData.policyHolderAge)
            newErrors.policyHolderAge = "Policy holder age is required";
          else if (
            isNaN(parseInt(formData.policyHolderAge)) ||
            parseInt(formData.policyHolderAge) < 18 ||
            parseInt(formData.policyHolderAge) > 100
          )
            newErrors.policyHolderAge = "Invalid age (must be 18-100)";
          if (!formData.policyHolderGender)
            newErrors.policyHolderGender = "Policy holder gender is required";
          if (!formData.relationshipWithPatient)
            newErrors.relationshipWithPatient = "Relationship is required";
        }

        // Mandatory: Policy holder DOB (no future date) and Alternate Mobile (10-digit)
        if (!formData.policyHolderDob) {
          newErrors.policyHolderDob = "Policy holder date of birth is required";
        } else {
          const entered = new Date(formData.policyHolderDob);
          const todayOnly = new Date();
          todayOnly.setHours(0,0,0,0);
          if (entered > todayOnly) {
            newErrors.policyHolderDob = "DOB cannot be in the future";
          }
        }

        if (!formData.policyHolderAlternatePhone || !formData.policyHolderAlternatePhone.trim()) {
          newErrors.policyHolderAlternatePhone = "Alternate mobile number is required";
        } else if (!/^[6-9]\d{9}$/.test(formData.policyHolderAlternatePhone.replace(/\s+/g, ""))) {
          newErrors.policyHolderAlternatePhone = "Invalid mobile number";
        }
        // Employment Information Validation
        if (!formData.employmentStatus)
          newErrors.employmentStatus = "Employment status is required";
        if (!formData.monthlyIncome)
          newErrors.monthlyIncome = "Monthly income is required";
        else if (parseFloat(formData.monthlyIncome) <= 0)
          newErrors.monthlyIncome = "Invalid income amount";

        // Unified validation regardless of policy type
        if (formData.employmentStatus === "employed") {
          if (!formData.workingCompanyName.trim())
            newErrors.workingCompanyName = "Working company name is required";
          if (!formData.designation.trim())
            newErrors.designation = "Designation is required";
        }
        if (formData.employmentStatus === "self-employed" || formData.employmentStatus === "business") {
          if (!formData.natureOfWork.trim())
            newErrors.natureOfWork = "Nature of work is required";
        }
        // Nominee details are optional - no validation required
        break;

      case 3: // KYC Details (text-only validations; files validated in step 7)
        // Aadhar - text only here
        if (!formData.patientAadhar.trim())
          newErrors.patientAadhar = "Aadhar number is required";
        else if (!/^\d{12}$/.test(formData.patientAadhar.replace(/\s+/g, "")))
          newErrors.patientAadhar = "Invalid Aadhar number";

        // PAN - required when policy holder is self; optional otherwise
        if (formData.isSelf) {
          if (!formData.patientPan || !formData.patientPan.trim()) {
            newErrors.patientPan = "PAN is required when patient is the policy holder";
          } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.patientPan.toUpperCase())) {
            newErrors.patientPan = "Invalid PAN format";
          }
        } else if (formData.patientPan && formData.patientPan.trim()) {
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.patientPan.toUpperCase())) {
            newErrors.patientPan = "Invalid PAN format";
          }
        }
        // Address Proof text optional
        break;

      case 4: // Medical History (documents validated in step 7)
        break;

      case 5: // Policy Information (documents validated in step 7)
        if (!formData.policyType)
          newErrors.policyType = "Policy type is required";
        if (!formData.policyNumber.trim())
          newErrors.policyNumber = "Policy number is required";
        if (!formData.policyStartDate)
          newErrors.policyStartDate = "Policy start date is required";
        if (!formData.policyEndDate)
          newErrors.policyEndDate = "Policy end date is required";

        // Validate policy duration
        if (formData.policyStartDate && formData.policyEndDate) {
          const durationValidation = validatePolicyDuration(formData.policyStartDate, formData.policyEndDate);
          if (!durationValidation.valid) {
            newErrors.policyEndDate = durationValidation.message;
          }
        }

        if (!formData.insuranceCompany)
          newErrors.insuranceCompany = "Insurance company is required";
        if (formData.policyType === "corporate") {
          if (!formData.corporateName || !formData.corporateName.trim()) {
            newErrors.corporateName = "Corporate name is required for corporate policy";
          }
        }
        // Employment validation moved to Step 2
        break;

      case 6: // Diagnosis Details
        if (treatments.length === 0)
          newErrors.treatments = "At least one diagnosis/treatment is required";
        if (!formData.treatmentType)
          newErrors.treatmentType = "Treatment type is required";
        if (!formData.doctorName.trim())
          newErrors.doctorName = "Doctor name is required";
        if (!formData.doctorDesignation.trim())
          newErrors.doctorDesignation = "Doctor designation is required";
        if (!formData.admissionDate)
          newErrors.admissionDate = "Admission date is required";
        else {
          // Check if admission date is within 3 days (unless super admin override)
          if (!isSuperAdmin || !showAdmissionDateOverride) {
            if (!isAdmissionDateValid(formData.admissionDate)) {
              newErrors.admissionDate = "Admission date should not be older than 3 days";
            }
          }

          // Check if admission date is within policy period
          if (formData.policyStartDate && formData.policyEndDate) {
            if (!isAdmissionWithinPolicyPeriod(formData.admissionDate, formData.policyStartDate, formData.policyEndDate)) {
              if (!isSuperAdmin || !showAdmissionDateOverride) {
                newErrors.admissionDate = "Admission date should be within policy period";
              }
            }
          }
        }
        if (!formData.estimatedCost)
          newErrors.estimatedCost = "Estimated cost is required";
        else if (parseFloat(formData.estimatedCost) <= 0)
          newErrors.estimatedCost = "Invalid cost amount";
        break;

      case 7: // Upload Documents (all uploads consolidated here)
        // Patient KYC docs
        if (!documents.patientAadharFrontDoc)
          newErrors.patientAadharFrontDoc = "Aadhar front side is required";
        if (!documents.patientAadharBackDoc)
          newErrors.patientAadharBackDoc = "Aadhar back side is required";
        // Address proof upload optional

        // Policy docs
        if (formData.isSelf) {
          if (!documents.patientChequeOrStatementDoc)
            newErrors.patientChequeOrStatementDoc = "Cancelled cheque or bank statement is required";
          if (!documents.patientPolicyCopyDocs || documents.patientPolicyCopyDocs.length === 0)
            newErrors.patientPolicyCopyDocs = "At least one policy copy is required";
          // PAN card upload required when self
          if (!documents.patientPanDoc)
            newErrors.patientPanDoc = "Patient PAN card is required (self as policy holder)";
        } else {
          if (!documents.policyHolderAadharFrontDoc)
            newErrors.policyHolderAadharFrontDoc = "Policyholder Aadhar front side is required";
          if (!documents.policyHolderAadharBackDoc)
            newErrors.policyHolderAadharBackDoc = "Policyholder Aadhar back side is required";
          if (!documents.policyHolderPanDoc)
            newErrors.policyHolderPanDoc = "Policyholder PAN card is required";
          if (!documents.policyHolderChequeOrStatementDoc)
            newErrors.policyHolderChequeOrStatementDoc = "Policyholder cancelled cheque or bank statement is required";
          if (!documents.policyHolderPolicyCopyDocs || documents.policyHolderPolicyCopyDocs.length === 0)
            newErrors.policyHolderPolicyCopyDocs = "At least one policyholder policy copy is required";
        }

        // Medical docs
        if (!documents.medicalDocs || documents.medicalDocs.length === 0) {
          newErrors.medicalDocs = "At least one medical document is required";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Function to handle lead generator selection
  const handleLeadGeneratorSelection = (leadGen) => {
    setSelectedLeadGenerator(leadGen);
    // Clear any previous lead generator error
    if (errors.leadGenerator) {
      setErrors((prev) => ({ ...prev, leadGenerator: "" }));
    }
  };

  const handleSubmit = async () => {
    // Validate the final upload step (7) before submit; jump back if invalid
    if (!validateStep(7)) {
      setCurrentStep(7);
      return;
    }

    // Require payment preference selection on Review & Submit step
    if (!formData.paymentPreference) {
      setErrors((prev) => ({ ...prev, paymentPreference: "Please choose a payment preference (PDC or Loan)" }));
      setCurrentStep(8);
      return;
    }

    // If no lead generators are available (hospital has no linked lead generator), proceed directly
    if (leadGenerators.length === 0) {
      await handleActualSubmit();
      return;
    }

    // Show lead selection dialog
    setShowLeadSelection(true);
  };

  const handleActualSubmit = async () => {
    // Only require lead generator selection if lead generators are available
    if (leadGenerators.length > 0 && !selectedLeadGenerator) {
      setErrors({ leadGenerator: "Please select a lead generator to proceed" });
      return;
    }

    setLoading(true);
    try {
      // Prepare documents for upload
      const documentsToUpload = {};
      Object.entries(documents).forEach(([key, file]) => {
        if (
          file &&
          key !== "treatmentDocs" &&
          key !== "medicalHistoryDocs" &&
          key !== "medicalDocs" &&
          key !== "investigationReports" &&
          key !== "patientPolicyCopyDocs" &&
          key !== "policyHolderPolicyCopyDocs"
        ) {
          documentsToUpload[key] = file;
        }
      });

      // Add treatment documents
      Object.entries(documents.treatmentDocs || {}).forEach(
        ([treatmentId, file]) => {
          if (file) {
            documentsToUpload[`treatmentDoc_${treatmentId}`] = file;
          }
        }
      );

      // Add medical history documents
      documents.medicalHistoryDocs.forEach((file, index) => {
        documentsToUpload[`medicalHistoryDoc_${index}`] = file;
      });

      // Add medical documents (mandatory section)
      documents.medicalDocs.forEach((file, index) => {
        documentsToUpload[`medicalDoc_${index}`] = file;
      });

      // Add investigation reports (optional)
      (documents.investigationReports || []).forEach((file, index) => {
        documentsToUpload[`investigationReport_${index}`] = file;
      });

      // Add policy copy documents (multiple)
      (documents.patientPolicyCopyDocs || []).forEach((file, index) => {
        documentsToUpload[`patientPolicyCopyDoc_${index}`] = file;
      });
      (documents.policyHolderPolicyCopyDocs || []).forEach((file, index) => {
        documentsToUpload[`policyHolderPolicyCopyDoc_${index}`] = file;
      });

      // Prepare final form data with lead generator info
      // Restore any cached employment values if current fields are blank but cache exists (user toggled statuses or steps)
      const cached = formData.__employmentCache;
      const restoredWorkingCompany = (!formData.workingCompanyName && formData.employmentStatus === 'employed' && cached?.employed?.workingCompanyName)
        ? cached.employed.workingCompanyName
        : formData.workingCompanyName;
      const restoredDesignation = (!formData.designation && formData.employmentStatus === 'employed' && cached?.employed?.designation)
        ? cached.employed.designation
        : formData.designation;
      const restoredNatureOfWork = (!formData.natureOfWork && (formData.employmentStatus === 'self-employed' || formData.employmentStatus === 'business') && (cached?.selfEmployed?.natureOfWork || cached?.business?.natureOfWork))
        ? (cached?.selfEmployed?.natureOfWork || cached?.business?.natureOfWork)
        : formData.natureOfWork;

      const finalFormData = {
        ...formData,
        workingCompanyName: restoredWorkingCompany,
        designation: restoredDesignation,
        natureOfWork: restoredNatureOfWork,
        paymentPreference: formData.paymentPreference || "", // ensure included
        treatments: treatments,
        estimatedCost: parseFloat(formData.estimatedCost) || 0,
        medicalHistoryDocsCount: documents.medicalHistoryDocs.length,
        medicalDocsCount: documents.medicalDocs.length,
        investigationReportsCount: (documents.investigationReports || []).length,
        patientPolicyCopyDocsCount: (documents.patientPolicyCopyDocs || []).length,
        policyHolderPolicyCopyDocsCount: (documents.policyHolderPolicyCopyDocs || []).length,

        // Employment Information - Explicitly map to ensure they're included
        employmentStatus: formData.employmentStatus || "",
        monthlyIncome: parseFloat(formData.monthlyIncome) || 0,

        // Lead Generator Information - Only include if a lead generator is selected
        leadGenerator: selectedLeadGenerator ? {
          id: selectedLeadGenerator.id || "",
          entityName: selectedLeadGenerator.entityName || "",
          city: selectedLeadGenerator.city || "",
          state: selectedLeadGenerator.state || "",
          servicesRequired: selectedLeadGenerator.servicesRequired || [],
          cinNumber: selectedLeadGenerator.cinNumber || "",
          leadGeneratorId: selectedLeadGenerator.leadGeneratorId || selectedLeadGenerator.id || "",
          assignedAt: new Date().toISOString(),
        } : {
          id: "",
          entityName: "",
          city: "",
          state: "",
          servicesRequired: [],
          cinNumber: "",
          leadGeneratorId: "",
          assignedAt: "",
          note: "No lead generator assigned - hospital has no linked lead generator"
        },
        // Ensure medical history fields are not undefined
        medicalHistory: formData.medicalHistory || "",
        medicalDiagnosis: formData.medicalDiagnosis || "",
        medicalDuration: formData.medicalDuration || "",
        surgicalHistory: formData.surgicalHistory || "",
        surgicalDiagnosis: formData.surgicalDiagnosis || "",
        surgicalDuration: formData.surgicalDuration || "",
        surgicalProcedure: formData.surgicalProcedure || "",
        corporateName: formData.corporateName || "",
      };

      // Clean up undefined values to prevent Firestore errors
      const cleanedFormData = Object.fromEntries(
        Object.entries(finalFormData).map(([key, value]) => [
          key,
          value === undefined ? "" : value
        ])
      );

      // Debug logging to verify data before submission
      console.log("Form data being submitted:", cleanedFormData);
      console.log("Employment fields:", {
        employmentStatus: cleanedFormData.employmentStatus,
        workingCompanyName: cleanedFormData.workingCompanyName,
        monthlyIncome: cleanedFormData.monthlyIncome,
        designation: cleanedFormData.designation,
        natureOfWork: cleanedFormData.natureOfWork
      });
      console.log("Lead generator data:", cleanedFormData.leadGenerator);

      const result = await createPatientApplication(
        cleanedFormData,
        documentsToUpload,
        hospitalId,
        userData?.hospitalName || "",
      );

      if (result.success) {
        setApplicationId(result.applicationId);
        setCaseId(result.caseId || "");
        setSubmitSuccess(true);
        setShowLeadSelection(false);
        // Clear auto-saved data on successful submission
        localStorage.removeItem('newPatientApplication');
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error("Application creation error:", error);
      setErrors({ submit: "Failed to create application. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const renderKycField = (fieldName, label, required = true, textRequired = true, docRequired = true) => {
    return (
      <div className="space-y-3">
        <label className="form-label">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {/* Show requirement info */}
        {textRequired && docRequired && (
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            <strong>Both details and document upload are required</strong>
          </p>
        )}
        {!textRequired && docRequired && (
          <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
            <strong>Document upload is required.</strong> Details are optional.
          </p>
        )}

        {/* Text Input Section */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Enter Details {textRequired && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            name={fieldName}
            value={formData[fieldName]}
            onChange={handleInputChange}
            className={`input-field ${errors[fieldName] ? "input-error" : ""}`}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
          {errors[fieldName] && <p className="form-error">{errors[fieldName]}</p>}
        </div>

        {/* File Upload Section (only when uploads are required in this context) */}
        {docRequired && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Upload Document <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              {!documents[`${fieldName}Doc`] ? (
                <div className="text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Upload {label} document
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      handleFileUpload(`${fieldName}Doc`, e.target.files[0])
                    }
                    className="hidden"
                    id={`${fieldName}Doc`}
                  />
                  <label
                    htmlFor={`${fieldName}Doc`}
                    className="btn-primary text-sm cursor-pointer"
                  >
                    Choose File
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-800">
                      {documents[`${fieldName}Doc`].name}
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(`${fieldName}Doc`)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {errors[`${fieldName}Doc`] && (
              <p className="form-error">{errors[`${fieldName}Doc`]}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Helper to render a document-only upload (no text field)
  const renderDocumentUpload = (docKey, label, required = true) => {
    return (
      <div className="space-y-3">
        <label className="form-label">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Upload Document {required && <span className="text-red-500">*</span>}
          </label>
          <div
            className={`border-2 border-dashed rounded-lg p-4 transition-colors ${dragOver[docKey] ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
            onDragOver={(e) => handleDragOverGeneric(e, docKey)}
            onDragLeave={(e) => handleDragLeaveGeneric(e, docKey)}
            onDrop={(e) => handleDropSingle(e, docKey)}
          >
            {!documents[docKey] ? (
              <div className="text-center">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Drag & drop {label} here, or click to browse</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(docKey, e.target.files[0])}
                  className="hidden"
                  id={docKey}
                />
                <label htmlFor={docKey} className="btn-primary text-sm cursor-pointer">
                  Choose File
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-800">{documents[docKey].name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => openPreview(documents[docKey])} className="text-primary-600 hover:text-primary-800 text-sm flex items-center">
                    <Eye className="h-4 w-4 mr-1" /> Preview
                  </button>
                  <button onClick={() => removeFile(docKey)} className="text-red-500 hover:text-red-700" title="Remove">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
          {errors[docKey] && <p className="form-error">{errors[docKey]}</p>}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    if (submitSuccess) {
      return (
        <div className="text-center space-y-6">
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Application Submitted Successfully!
            </h3>
            <p className="text-gray-600 mb-4">
              Patient application has been submitted for processing.
            </p>
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <p className="text-sm text-primary-800">
                <strong>Application ID:</strong> {applicationId}
              </p>
              {caseId && (
                <p className="text-sm text-primary-800 mt-1">
                  <strong>Case ID:</strong> {caseId}
                </p>
              )}
              {selectedLeadGenerator && (
                <p className="text-sm text-primary-800 mt-1">
                  <strong>Assigned Lead Generator:</strong> {selectedLeadGenerator.entityName}
                </p>
              )}
              <p className="text-sm text-primary-600 mt-2">
                You can track the status of this application in your dashboard.
              </p>
            </div>
          </div>

          <div className="flex space-x-4 justify-center">
            <button
              onClick={() => {
                localStorage.removeItem('newPatientApplication');
                window.location.reload();
              }}
              className="btn-primary"
            >
              Create New Application
            </button>
            <button onClick={onBack || (() => goBackToDashboard(navigate))} className="btn-outline">
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1: // Patient Information
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Patient Information
              </h4>
              <p className="text-sm text-primary-600">
                Enter the basic details of the patient
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Patient Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientName ? "input-error" : ""
                    }`}
                  placeholder="Enter patient full name"
                />
                {errors.patientName && (
                  <p className="form-error">{errors.patientName}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Age / Date of Birth <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="patientAge"
                    value={formData.patientAge}
                    onChange={handleInputChange}
                    onBlur={(e) => handleAgeBlur('patientAge', e.target.value)}
                    className={`input-field pr-10 ${errors.patientAge ? "input-error" : ""
                      }`}
                    placeholder="Enter age (e.g., 34) or DOB (12-06-2000)"
                  />
                  <input
                    ref={(input) => { if (input) window.patientDatePicker = input; }}
                    type="date"
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const dob = e.target.value;
                      const age = calculateAge(dob);
                      handleInputChange({ target: { name: 'patientAge', value: age.toString() } });
                    }}
                    style={{ 
                      position: 'absolute', 
                      right: '3rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      opacity: 0,
                      pointerEvents: 'none',
                      width: '1px',
                      height: '1px'
                    }}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (window.patientDatePicker) {
                          // Try modern showPicker() first, fallback to click()
                          if (window.patientDatePicker.showPicker) {
                            window.patientDatePicker.showPicker();
                          } else {
                            window.patientDatePicker.click();
                          }
                        }
                      }}
                      className="text-gray-500 hover:text-primary-600 transition-colors"
                      title="Select date of birth from calendar"
                    >
                      <Calendar className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {errors.patientAge && (
                  <p className="form-error">{errors.patientAge}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Enter age as number (e.g., 34) or date of birth in format DD-MM-YYYY
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  name="patientGender"
                  value={formData.patientGender}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientGender ? "input-error" : ""
                    }`}
                >
                  <option value="">Select Gender</option>
                  {genderOptions.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
                {errors.patientGender && (
                  <p className="form-error">{errors.patientGender}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="patientPhone"
                  value={formData.patientPhone}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientPhone ? "input-error" : ""
                    }`}
                  placeholder="+91 XXXXX XXXXX"
                />
                {errors.patientPhone && (
                  <p className="form-error">{errors.patientPhone}</p>
                )}
              </div>
            </div>

            <div>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                name="patientEmail"
                value={formData.patientEmail}
                onChange={handleInputChange}
                className={`input-field ${errors.patientEmail ? "input-error" : ""
                  }`}
                placeholder="patient@example.com"
              />
              {errors.patientEmail && (
                <p className="form-error">{errors.patientEmail}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="patientAddress"
                value={formData.patientAddress}
                onChange={handleInputChange}
                className={`input-field ${errors.patientAddress ? "input-error" : ""
                  }`}
                rows="3"
                placeholder="Enter complete address"
              />
              {errors.patientAddress && (
                <p className="form-error">{errors.patientAddress}</p>
              )}
            </div>
          </div>
        );

      case 2: // Policy Holder Information + Employment
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Policy Holder Information
              </h4>
              <p className="text-sm text-primary-600">
                Specify who holds the insurance policy
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isSelf"
                name="isSelf"
                checked={formData.isSelf}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <label
                htmlFor="isSelf"
                className="text-sm font-medium text-gray-700"
              >
                Patient is the policy holder
              </label>
            </div>

            {!formData.isSelf && (
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Policy Holder Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="policyHolderName"
                      value={formData.policyHolderName}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderName ? "input-error" : ""}
                        `}
                      placeholder="Enter policy holder name"
                    />
                    {errors.policyHolderName && (
                      <p className="form-error">{errors.policyHolderName}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Age / Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="policyHolderAge"
                        value={formData.policyHolderAge}
                        onChange={handleInputChange}
                        onBlur={(e) => handleAgeBlur('policyHolderAge', e.target.value)}
                        className={`input-field pr-10 ${errors.policyHolderAge ? "input-error" : ""}
                          `}
                        placeholder="Enter age (e.g., 45) or DOB (15-03-1980)"
                      />
                      <input
                        ref={(input) => { if (input) window.policyHolderDatePicker = input; }}
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(e) => {
                          const dob = e.target.value;
                          const age = calculateAge(dob);
                          handleInputChange({ target: { name: 'policyHolderAge', value: age.toString() } });
                        }}
                        style={{ 
                          position: 'absolute', 
                          right: '3rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          opacity: 0,
                          pointerEvents: 'none',
                          width: '1px',
                          height: '1px'
                        }}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (window.policyHolderDatePicker) {
                              // Try modern showPicker() first, fallback to click()
                              if (window.policyHolderDatePicker.showPicker) {
                                window.policyHolderDatePicker.showPicker();
                              } else {
                                window.policyHolderDatePicker.click();
                              }
                            }
                          }}
                          className="text-gray-500 hover:text-primary-600 transition-colors"
                          title="Select date of birth from calendar"
                        >
                          <Calendar className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    {errors.policyHolderAge && (
                      <p className="form-error">{errors.policyHolderAge}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Enter age as number (e.g., 45) or date of birth in format DD-MM-YYYY
                    </p>
                  </div>
                </div>

                {/* New: Policy Holder DOB and Alternate Mobile (Mandatory) */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Policy Holder Date of Birth <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      name="policyHolderDob"
                      value={formData.policyHolderDob}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                    {errors.policyHolderDob && (
                      <p className="form-error">{errors.policyHolderDob}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Alternate Mobile Number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      name="policyHolderAlternatePhone"
                      value={formData.policyHolderAlternatePhone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder=" alternate number"
                    />
                    {errors.policyHolderAlternatePhone && (
                      <p className="form-error">{errors.policyHolderAlternatePhone}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="policyHolderGender"
                      value={formData.policyHolderGender}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderGender ? "input-error" : ""}
                        `}
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map((gender) => (
                        <option key={gender} value={gender}>
                          {gender}
                        </option>
                      ))}
                    </select>
                    {errors.policyHolderGender && (
                      <p className="form-error">{errors.policyHolderGender}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Relationship with Patient{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="relationshipWithPatient"
                      value={formData.relationshipWithPatient}
                      onChange={handleInputChange}
                      className={`input-field ${errors.relationshipWithPatient ? "input-error" : ""}
                        `}
                    >
                      <option value="">Select Relationship</option>
                      {relationshipOptions
                        .filter((rel) => rel !== "Self")
                        .map((relationship) => (
                          <option key={relationship} value={relationship}>
                            {relationship}
                          </option>
                        ))}
                    </select>
                    {errors.relationshipWithPatient && (
                      <p className="form-error">
                        {errors.relationshipWithPatient}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      name="policyHolderPhone"
                      value={formData.policyHolderPhone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      name="policyHolderEmail"
                      value={formData.policyHolderEmail}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="policyholder@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Address</label>
                  <textarea
                    name="policyHolderAddress"
                    value={formData.policyHolderAddress}
                    onChange={handleInputChange}
                    className="input-field"
                    rows="3"
                    placeholder="Enter complete address"
                  />
                </div>

                {/* Policy Holder KYC (details only; uploads in step 7) */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h5 className="font-medium text-gray-800 mb-4">
                    Policy Holder KYC Details
                  </h5>
                  <div className="space-y-4">
                    {renderKycField(
                      "policyHolderAadhar",
                      "Aadhar Number",
                      false,
                      false,
                      false
                    )}
                    {renderKycField("policyHolderPan", "PAN Number", false, false, false)}
                    {renderKycField(
                      "policyHolderAddressProof",
                      "Address Proof",
                      false,
                      false,
                      false
                    )}
                  </div>
                </div>
              </>
            )}

            {formData.isSelf && (
              // When patient is policy holder, show only the two new fields
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Policy Holder Date of Birth <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="policyHolderDob"
                    value={formData.policyHolderDob}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                  {errors.policyHolderDob && (
                    <p className="form-error">{errors.policyHolderDob}</p>
                  )}
                </div>
                <div>
                  <label className="form-label">Alternate Mobile Number <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    name="policyHolderAlternatePhone"
                    value={formData.policyHolderAlternatePhone}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="alternate number"
                  />
                  {errors.policyHolderAlternatePhone && (
                    <p className="form-error">{errors.policyHolderAlternatePhone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Nominee Information */}
            <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
              <h5 className="font-semibold text-secondary-800 mb-2">
                Nominee Information (Optional)
              </h5>
              <p className="text-sm text-secondary-600 mb-4">
                Person who will receive benefits in case of unfortunate events. These details are optional and can be added later.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">
                    Nominee Name
                  </label>
                  <input
                    type="text"
                    name="nomineeName"
                    value={formData.nomineeName}
                    onChange={handleInputChange}
                    className={`input-field ${errors.nomineeName ? "input-error" : ""}
                      `}
                    placeholder="Enter nominee name"
                  />
                  {errors.nomineeName && (
                    <p className="form-error">{errors.nomineeName}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Relationship with Nominee
                  </label>
                  <select
                    name="relationshipWithNominee"
                    value={formData.relationshipWithNominee}
                    onChange={handleInputChange}
                    className={`input-field ${errors.relationshipWithNominee ? "input-error" : ""}
                      `}
                  >
                    <option value="">Select Relationship</option>
                    {relationshipOptions.map((relationship) => (
                      <option key={relationship} value={relationship}>
                        {relationship}
                      </option>
                    ))}
                  </select>
                  {errors.relationshipWithNominee && (
                    <p className="form-error">
                      {errors.relationshipWithNominee}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">
                  Nominee Contact
                </label>
                <input
                  type="tel"
                  name="nomineeContact"
                  value={formData.nomineeContact}
                  onChange={handleInputChange}
                  className={`input-field ${errors.nomineeContact ? "input-error" : ""}
                    `}
                  placeholder="+91 XXXXX XXXXX"
                />
                {errors.nomineeContact && (
                  <p className="form-error">{errors.nomineeContact}</p>
                )}
              </div>
            </div>

            {/* Employment Information Section moved here */}
            <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
              <h4 className="font-semibold text-secondary-800 mb-4">
                Employment Information
              </h4>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Employment Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="employmentStatus"
                      value={formData.employmentStatus}
                      onChange={handleInputChange}
                      className={`input-field ${errors.employmentStatus ? "input-error" : ""}
                        `}
                    >
                      <option value="">Select Employment Status</option>
                      <option value="employed">Employed</option>
                      <option value="self-employed">Self-employed</option>
                      <option value="business">Business</option>
                      <option value="unemployed">Not Employed</option>
                    </select>
                    {errors.employmentStatus && (
                      <p className="form-error">{errors.employmentStatus}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Monthly Income (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="monthlyIncome"
                      value={formData.monthlyIncome}
                      onChange={handleInputChange}
                      className={`input-field ${errors.monthlyIncome ? "input-error" : ""}
                        `}
                      placeholder="Enter monthly income"
                      min="0"
                    />
                    {errors.monthlyIncome && (
                      <p className="form-error">{errors.monthlyIncome}</p>
                    )}
                  </div>
                </div>

                {/* Conditional fields for Employed */}
                {formData.employmentStatus === "employed" && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">
                        Working Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="workingCompanyName"
                        value={formData.workingCompanyName}
                        onChange={handleInputChange}
                        className={`input-field ${errors.workingCompanyName ? "input-error" : ""}
                          `}
                        placeholder="Enter company name"
                      />
                      {errors.workingCompanyName && (
                        <p className="form-error">{errors.workingCompanyName}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className={`input-field ${errors.designation ? "input-error" : ""}
                          `}
                        placeholder="e.g., Manager, Executive, Officer"
                      />
                      {errors.designation && (
                        <p className="form-error">{errors.designation}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Nature of work for Self-employed or Business */}
                {(formData.employmentStatus === "self-employed" || formData.employmentStatus === "business") && (
                  <div className="grid md:grid-cols-1 gap-6">
                    <div>
                      <label className="form-label">
                        Nature of Work <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="natureOfWork"
                        value={formData.natureOfWork}
                        onChange={handleInputChange}
                        className={`input-field ${errors.natureOfWork ? "input-error" : ""}
                          `}
                        placeholder="e.g., Business, Freelancing, Consulting"
                      />
                      {errors.natureOfWork && (
                        <p className="form-error">{errors.natureOfWork}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 3: // KYC Documents (details only)
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Patient Verification Documents
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    <strong>Requirements:</strong>
                  </p>
                  <ul className="text-sm text-amber-700 mt-2 space-y-1">
                    <li>• <strong>Aadhar:</strong> Aadhar number and card (front & back) are required</li>
                    <li>• <strong>PAN:</strong> Mandatory when patient is policy holder (Self); optional otherwise</li>
                    <li>• <strong>Address Proof:</strong> Optional (provide if available)</li>
                  </ul>
                </div>
              </div>
            </div>

            {renderKycField("patientAadhar", "Aadhar Number", true, true, false)}
            {renderKycField(
              "patientPan",
              "PAN Number",
              formData.isSelf, // required only when patient is the policy holder
              formData.isSelf, // text entry required only when self
              false // no upload in this step
            )}
            {renderKycField("patientAddressProof", "Address Proof", false, false, false)}
          </div>
        );

      case 4: // Medical History (documents moved to Upload step)
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Patient Medical History
              </h4>
              <p className="text-sm text-primary-600">
                Provide details about the patient's medical and surgical history
              </p>
            </div>

            {/* Checkbox for Medical History */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="hasMedicalHistory"
                name="hasMedicalHistory"
                checked={formData.hasMedicalHistory}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <label
                htmlFor="hasMedicalHistory"
                className="text-sm font-medium text-gray-700"
              >
                Patient has previous medical history
              </label>
            </div>

            {formData.hasMedicalHistory ? (
              <>
                {/* Medical History Section */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h5 className="font-semibold text-blue-800 mb-4">Medical History</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Medical History Description</label>
                      <textarea
                        name="medicalHistory"
                        value={formData.medicalHistory}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Previous medical conditions, chronic diseases, medications, etc."
                      />
                    </div>
                    <div>
                      <label className="form-label">Diagnosis Information</label>
                      <textarea
                        name="medicalDiagnosis"
                        value={formData.medicalDiagnosis}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Specific diagnosis details, medical conditions, etc."
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="form-label">Duration</label>
                    <input
                      type="text"
                      name="medicalDuration"
                      value={formData.medicalDuration}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="e.g., 2 years, 6 months, ongoing"
                    />
                  </div>
                </div>

                {/* Surgical History Section */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h5 className="font-semibold text-purple-800 mb-4">Surgical History</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Surgical History Description</label>
                      <textarea
                        name="surgicalHistory"
                        value={formData.surgicalHistory}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Previous surgeries, operations, procedures, etc."
                      />
                    </div>
                    <div>
                      <label className="form-label">Surgical Diagnosis Information</label>
                      <textarea
                        name="surgicalDiagnosis"
                        value={formData.surgicalDiagnosis}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Diagnosis that led to surgery, surgical indications, etc."
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label className="form-label">Duration</label>
                      <input
                        type="text"
                        name="surgicalDuration"
                        value={formData.surgicalDuration}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="e.g., 1 year ago, 3 months ago"
                      />
                    </div>
                    <div>
                      <label className="form-label">Procedure Information</label>
                      <input
                        type="text"
                        name="surgicalProcedure"
                        value={formData.surgicalProcedure}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="Name of surgical procedure performed"
                      />
                    </div>
                  </div>
                </div>

                {/* Medical History Documents moved to Upload step */}
              </>
            ) : (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">
                      No Medical History Declared
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      <strong>Warning:</strong> You have declared that the patient has no previous medical history.
                      If this information is found to be incorrect or misleading during the claim process,
                      the application may be rejected and you will be held responsible for providing false information.
                    </p>
                    <p className="text-sm text-red-600 mt-2 font-medium">
                      Please ensure this declaration is accurate and complete.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Medical Documents moved to Upload step */}
          </div>
        );

      case 5: // Policy Information (moved before diagnosis; uploads moved to Upload step)
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Insurance Policy Information
              </h4>
              <p className="text-sm text-primary-600">
                Enter the details of the insurance policy
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Policy Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="policyType"
                  value={formData.policyType}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyType ? "input-error" : ""
                    }`}
                >
                  <option value="">Select Policy Type</option>
                  <option value="retail">Retail</option>
                  <option value="corporate">Corporate</option>
                </select>
                {errors.policyType && (
                  <p className="form-error">{errors.policyType}</p>
                )}
              </div>

              {formData.policyType === "corporate" && (
                <div>
                  <label className="form-label">
                    Corporate Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="corporateName"
                    value={formData.corporateName}
                    onChange={handleInputChange}
                    className={`input-field ${errors.corporateName ? "input-error" : ""}`}
                    placeholder="Enter corporate/employer name"
                  />
                  {errors.corporateName && (
                    <p className="form-error">{errors.corporateName}</p>
                  )}
                </div>
              )}

              <div>
                <label className="form-label">
                  Policy Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="policyNumber"
                  value={formData.policyNumber}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyNumber ? "input-error" : ""
                    }`}
                  placeholder="Enter policy number"
                />
                {errors.policyNumber && (
                  <p className="form-error">{errors.policyNumber}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Policy Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="policyStartDate"
                  value={formData.policyStartDate}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyStartDate ? "input-error" : ""
                    }`}
                />
                {errors.policyStartDate && (
                  <p className="form-error">{errors.policyStartDate}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Policy End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="policyEndDate"
                  value={formData.policyEndDate}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyEndDate ? "input-error" : ""
                    }`}
                />
                {errors.policyEndDate && (
                  <p className="form-error">{errors.policyEndDate}</p>
                )}
              </div>
            </div>

            {formData.policyType === 'corporate' && formData.corporateName && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    Corporate: <strong>{formData.corporateName}</strong>
                  </span>
                </div>
              </div>
            )}

            {/* Policy Duration and Expiry Validation Messages */}
            {formData.policyStartDate && formData.policyEndDate && (
              <div className="space-y-3">
                {(() => {
                  const durationValidation = validatePolicyDuration(formData.policyStartDate, formData.policyEndDate);
                  const expiryStatus = checkPolicyExpiry(formData.policyEndDate);

                  return (
                    <>
                      {/* Duration Validation */}
                      {durationValidation.valid ? (
                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-800 font-medium">
                              {durationValidation.message}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm text-red-800 font-medium">
                              {durationValidation.message}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Expiry Status */}
                      {expiryStatus.status === 'expired' && (
                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                            <div>
                              <p className="text-sm text-red-800 font-medium">Policy Expired</p>
                              <p className="text-sm text-red-700 mt-1">{expiryStatus.message}</p>
                              <div className="mt-3 flex items-center space-x-3">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name="policyRenewalStatus"
                                    value="renewed"
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Policy has been renewed</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name="policyRenewalStatus"
                                    value="inprocess"
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Renewal in process</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {expiryStatus.status === 'expiring' && (
                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="text-sm text-amber-800 font-medium">Policy Expiring Soon</p>
                              <p className="text-sm text-amber-700 mt-1">{expiryStatus.message}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Insurance Company <span className="text-red-500">*</span>
                </label>
                <select
                  name="insuranceCompany"
                  value={formData.insuranceCompany}
                  onChange={handleInputChange}
                  className={`input-field ${errors.insuranceCompany ? "input-error" : ""
                    }`}
                >
                  <option value="">Select Insurance Company</option>
                  {insuranceCompanies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
                {errors.insuranceCompany && (
                  <p className="form-error">{errors.insuranceCompany}</p>
                )}
              </div>

              <div>
                <label className="form-label">TPA Name</label>
                <select
                  name="tpaName"
                  value={formData.tpaName}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="">Select TPA Company</option>
                  {tpaCompanies.map((tpa) => (
                    <option key={tpa} value={tpa}>
                      {tpa}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Employment Information moved to Step 2 */}

            {/* Conditional Policy Document Uploads moved to Upload step */}
          </div>
        );

      case 6: // Diagnosis Details
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Medical Diagnosis & Treatment Details
              </h4>
              <p className="text-sm text-primary-600">
                Add multiple diagnoses and treatments for the patient
              </p>
            </div>

            {/* Add New Treatment Section */}
            <div className="bg-secondary-50 rounded-lg p-6 border border-secondary-200">
              <h5 className="font-semibold text-secondary-800 mb-4">
                Add Diagnosis & Treatment
              </h5>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Diagnosis Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newTreatment.diagnosisName}
                      onChange={(e) =>
                        setNewTreatment((prev) => ({
                          ...prev,
                          diagnosisName: e.target.value,
                        }))
                      }
                      className={`input-field ${errors.diagnosisName ? "input-error" : ""
                        }`}
                      placeholder="Enter diagnosis name"
                    />
                    {errors.diagnosisName && (
                      <p className="form-error">{errors.diagnosisName}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Treatment Type</label>
                    <select
                      value={newTreatment.treatmentType}
                      onChange={(e) =>
                        setNewTreatment((prev) => ({
                          ...prev,
                          treatmentType: e.target.value,
                        }))
                      }
                      className="input-field"
                    >
                      <option value="">Select Treatment Type</option>
                      {treatmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Procedure Name</label>
                    <input
                      type="text"
                      value={newTreatment.procedureName}
                      onChange={(e) =>
                        setNewTreatment((prev) => ({
                          ...prev,
                          procedureName: e.target.value,
                        }))
                      }
                      className="input-field"
                      placeholder="Enter procedure name"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={addTreatment}
                      className="btn-primary w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Treatment
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label">Description</label>
                  <textarea
                    value={newTreatment.description}
                    onChange={(e) =>
                      setNewTreatment((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="input-field"
                    rows="2"
                    placeholder="Additional details about the treatment"
                  />
                </div>
              </div>
            </div>

            {/* Display Added Treatments */}
            {treatments.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h5 className="font-medium text-gray-800 mb-4">
                  Added Diagnoses & Treatments
                </h5>
                <div className="space-y-3">
                  {treatments.map((treatment) => (
                    <div
                      key={treatment.id}
                      className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Diagnosis
                              </span>
                              <p className="text-sm font-semibold text-gray-900 mt-1">
                                {treatment.diagnosisName}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Treatment
                              </span>
                              <p className="text-sm text-gray-700 mt-1">
                                {treatment.treatmentType || "Not specified"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Procedure
                              </span>
                              <p className="text-sm text-gray-700 mt-1">
                                {treatment.procedureName || "Not specified"}
                              </p>
                            </div>
                          </div>
                          {treatment.description && (
                            <div className="mt-3">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Description
                              </span>
                              <p className="text-sm text-gray-700 mt-1">
                                {treatment.description}
                              </p>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => removeTreatment(treatment.id)}
                          className="ml-4 text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Treatment Document Upload */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Treatment Document
                        </label>
                        {!documents.treatmentDocs?.[treatment.id] ? (
                          <div className="mt-2">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleTreatmentDocUpload(
                                  treatment.id,
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id={`treatmentDoc_${treatment.id}`}
                            />
                            <label
                              htmlFor={`treatmentDoc_${treatment.id}`}
                              className="cursor-pointer inline-flex items-center text-xs text-secondary-600 hover:text-secondary-800"
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Document
                            </label>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-green-800">
                              {documents.treatmentDocs[treatment.id].name}
                            </span>
                            <button
                              onClick={() => {
                                setDocuments((prev) => {
                                  const newDocs = { ...prev };
                                  delete newDocs.treatmentDocs[treatment.id];
                                  return newDocs;
                                });
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {errors[`treatmentDoc_${treatment.id}`] && (
                          <p className="text-xs text-red-600 mt-1">
                            {errors[`treatmentDoc_${treatment.id}`]}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Treatment validation error */}
            {errors.treatments && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <p className="text-sm text-red-800">{errors.treatments}</p>
              </div>
            )}

            {/* Additional Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h5 className="font-medium text-gray-800 mb-4">
                Treatment Information
              </h5>

              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <label className="form-label">Treatment Plan</label>
                  <select
                    name="treatmentPlanned"
                    value={formData.treatmentPlanned}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="planned">Planned</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Priority Level</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

             
            </div>

            {/* Doctor and Hospital Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Doctor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleInputChange}
                  className={`input-field ${errors.doctorName ? "input-error" : ""
                    }`}
                  placeholder="Dr. Full Name"
                />
                {errors.doctorName && (
                  <p className="form-error">{errors.doctorName}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Doctor Designation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="doctorDesignation"
                  value={formData.doctorDesignation}
                  onChange={handleInputChange}
                  className={`input-field ${errors.doctorDesignation ? "input-error" : ""
                    }`}
                  placeholder="e.g., Cardiologist, Surgeon"
                />
                {errors.doctorDesignation && (
                  <p className="form-error">{errors.doctorDesignation}</p>
                )}
              </div>
            </div>

            {/* Admission and Discharge Dates */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Admission Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleInputChange}
                  className={`input-field ${errors.admissionDate ? "input-error" : ""
                    }`}
                />
                {errors.admissionDate && (
                  <p className="form-error">{errors.admissionDate}</p>
                )}

                {/* Super Admin Override Option */}
                {isSuperAdmin && (
                  <div className="mt-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={showAdmissionDateOverride}
                        onChange={(e) => setShowAdmissionDateOverride(e.target.checked)}
                        className="mr-2 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-amber-700">Override date restrictions (Admin)</span>
                    </label>
                  </div>
                )}

                {formData.admissionDate && !isAdmissionDateValid(formData.admissionDate) && !showAdmissionDateOverride && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>Warning: Admission date is older than 3 days</span>
                    </div>
                  </div>
                )}

                {formData.admissionDate && formData.policyStartDate && formData.policyEndDate &&
                  !isAdmissionWithinPolicyPeriod(formData.admissionDate, formData.policyStartDate, formData.policyEndDate) &&
                  !showAdmissionDateOverride && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      <div className="flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        <span>Error: Admission date is outside policy period</span>
                      </div>
                    </div>
                  )}
              </div>

              <div>
                <label className="form-label">Discharge Date</label>
                <input
                  type="date"
                  name="dischargeDate"
                  value={formData.dischargeDate}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>
            </div>

            {/* Cost Information */}
            <div>
              <label className="form-label">
                Estimated Cost (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="estimatedCost"
                value={formData.estimatedCost}
                onChange={handleInputChange}
                className={`input-field ${errors.estimatedCost ? "input-error" : ""
                  }`}
                placeholder="Enter estimated treatment cost"
                min="0"
              />
              {errors.estimatedCost && (
                <p className="form-error">{errors.estimatedCost}</p>
              )}
            </div>

            {/* Remarks */}
            <div>
              <label className="form-label">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                className="input-field"
                rows="3"
                placeholder="Any additional remarks or notes about the case"
              />
            </div>
          </div>
        );

      case 8: // Review & Submit
        return (
          <div className="space-y-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-800">
                    Review Application
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Please review all information before submitting the
                    application
                  </p>
                </div>
              </div>
            </div>

            {/* Payment / Financing Preference */}
            <div className="card">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-primary-500" />
                Payment / Financing Preference
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                Choose how the case will proceed for disbursal: provide Post Dated Cheques (PDC) or opt for a Loan route. This helps the financial team plan the next steps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <label className={`flex-1 border rounded-lg p-4 cursor-pointer transition hover:shadow-sm ${formData.paymentPreference === 'pdc' ? 'border-primary-500 ring-1 ring-primary-300' : 'border-gray-200'}`}> 
                  <input
                    type="radio"
                    name="paymentPreference"
                    value="pdc"
                    checked={formData.paymentPreference === 'pdc'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <div>
                    <div className="font-medium">PDC (Post Dated Cheques)</div>
                    <div className="text-xs text-gray-500 mt-1">Hospital / patient will provide post dated cheques for instalments.</div>
                  </div>
                </label>
                <label className={`flex-1 border rounded-lg p-4 cursor-pointer transition hover:shadow-sm ${formData.paymentPreference === 'loan' ? 'border-primary-500 ring-1 ring-primary-300' : 'border-gray-200'}`}> 
                  <input
                    type="radio"
                    name="paymentPreference"
                    value="loan"
                    checked={formData.paymentPreference === 'loan'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <div>
                    <div className="font-medium">Loan</div>
                    <div className="text-xs text-gray-500 mt-1">Proceed via loan processing workflow (verification & sanction).</div>
                  </div>
                </label>
              </div>
              {errors.paymentPreference && (
                <p className="form-error mt-2">{errors.paymentPreference}</p>
              )}
            </div>

            {/* Review Summary */}
            <div className="space-y-4">
              {/* Patient Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2 text-primary-500" />
                  Patient Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {formData.patientName}
                  </div>
                  <div>
                    <span className="font-medium">Age:</span>{" "}
                    {formData.patientAge} years
                  </div>
                  <div>
                    <span className="font-medium">Gender:</span>{" "}
                    {formData.patientGender}
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span>{" "}
                    {formData.patientPhone}
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium">Address:</span>{" "}
                    {formData.patientAddress}
                  </div>
                </div>
              </div>

              {/* Medical History */}
              {(formData.medicalHistory || formData.surgicalHistory || documents.medicalHistoryDocs.length > 0) && (
                <div className="card">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <History className="h-5 w-5 mr-2 text-primary-500" />
                    Medical History
                  </h4>
                  <div className="space-y-2 text-sm">
                    {formData.medicalHistory && (
                      <div>
                        <span className="font-medium">Medical History:</span>{" "}
                        {formData.medicalHistory}
                      </div>
                    )}
                    {formData.surgicalHistory && (
                      <div>
                        <span className="font-medium">Surgical History:</span>{" "}
                        {formData.surgicalHistory}
                      </div>
                    )}
                    {documents.medicalHistoryDocs.length > 0 && (
                      <div>
                        <span className="font-medium">Documents:</span> {documents.medicalHistoryDocs.length} files uploaded
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Policy Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-primary-500" />
                  Policy Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Policy Type:</span>{" "}
                    {formData.policyType}
                  </div>
                  <div>
                    <span className="font-medium">Policy Number:</span>{" "}
                    {formData.policyNumber}
                  </div>
                  <div>
                    <span className="font-medium">Insurance Company:</span>{" "}
                    {formData.insuranceCompany}
                  </div>
                  <div>
                    <span className="font-medium">Policy Period:</span>{" "}
                    {formData.policyStartDate} to {formData.policyEndDate}
                  </div>
                  {formData.tpaName && (
                    <div>
                      <span className="font-medium">TPA:</span>{" "}
                      {formData.tpaName}
                    </div>
                  )}
                </div>
              </div>

              {/* Employment Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-primary-500" />
                  Employment Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Employment Status:</span>{" "}
                    {formData.employmentStatus.charAt(0).toUpperCase() + formData.employmentStatus.slice(1)}
                  </div>
                  <div>
                    <span className="font-medium">Monthly Income:</span> ₹
                    {parseFloat(formData.monthlyIncome || 0).toLocaleString()}
                  </div>
                  {formData.policyType === "retail" && formData.workingCompanyName && (
                    <div>
                      <span className="font-medium">Company:</span>{" "}
                      {formData.workingCompanyName}
                    </div>
                  )}
                  {formData.employmentStatus === "self-employed" && formData.natureOfWork && (
                    <div>
                      <span className="font-medium">Nature of Work:</span>{" "}
                      {formData.natureOfWork}
                    </div>
                  )}
                  {formData.employmentStatus === "employed" && formData.designation && (
                    <div>
                      <span className="font-medium">Designation:</span>{" "}
                      {formData.designation}
                    </div>
                  )}
                </div>
              </div>

              {/* Medical Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Stethoscope className="h-5 w-5 mr-2 text-primary-500" />
                  Medical Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Diagnosis:</span>{" "}
                    {formData.diagnosis}
                  </div>
                  <div>
                    <span className="font-medium">Treatment Type:</span>{" "}
                    {formData.treatmentType}
                  </div>
                  <div>
                    <span className="font-medium">Doctor:</span> Dr.{" "}
                    {formData.doctorName}
                  </div>
                  <div>
                    <span className="font-medium">Designation:</span>{" "}
                    {formData.doctorDesignation}
                  </div>
                  <div>
                    <span className="font-medium">Admission Date:</span>{" "}
                    {formData.admissionDate}
                  </div>
                  <div>
                    <span className="font-medium">Estimated Cost:</span> ₹
                    {parseFloat(formData.estimatedCost || 0).toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Treatment Plan:</span>{" "}
                    {formData.treatmentPlanned}
                  </div>
                  <div>
                    <span className="font-medium">Priority:</span>{" "}
                    {formData.priority}
                  </div>
                </div>
                {treatments.length > 0 && (
                  <div className="mt-4">
                    <span className="font-medium text-sm">
                      Additional Diagnoses ({treatments.length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                      {treatments.map((treatment) => (
                        <li key={treatment.id}>{treatment.diagnosisName}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Policy Holder Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-primary-500" />
                  Policy Holder & Nominee
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Policy Holder:</span>{" "}
                    {formData.isSelf
                      ? "Self (Patient)"
                      : formData.policyHolderName}
                  </div>
                  {!formData.isSelf && (
                    <div>
                      <span className="font-medium">Relationship:</span>{" "}
                      {formData.relationshipWithPatient}
                    </div>
                  )}
                  {formData.policyHolderDob && (
                    <div>
                      <span className="font-medium">Policy Holder DOB:</span>{" "}
                      {formData.policyHolderDob}
                    </div>
                  )}
                  {formData.policyHolderAlternatePhone && (
                    <div>
                      <span className="font-medium">Alternate Mobile:</span>{" "}
                      {formData.policyHolderAlternatePhone}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Nominee:</span>{" "}
                    {formData.nomineeName}
                  </div>
                  <div>
                    <span className="font-medium">Nominee Relationship:</span>{" "}
                    {formData.relationshipWithNominee}
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary-500" />
                  Documents Uploaded
                </h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {Object.entries(documents).map(([key, file]) => {
                    if (key === "treatmentDocs") {
                      return Object.entries(file || {}).map(
                        ([treatmentId, treatmentFile]) =>
                          treatmentFile && (
                            <div
                              key={treatmentId}
                              className="flex items-center space-x-2"
                            >
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span>Treatment Doc: {treatmentFile.name}</span>
                            </div>
                          )
                      );
                    }
                    if (key === "medicalHistoryDocs") {
                      return file.map((historyFile, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Medical History: {historyFile.name}</span>
                        </div>
                      ));
                    }
                    if (key === "medicalDocs") {
                      return file.map((medFile, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Medical Document: {medFile.name}</span>
                        </div>
                      ));
                    }
                    return (
                      file && (
                        <div key={key} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>{file.name}</span>
                        </div>
                      )
                    );
                  })}
                </div>
              </div>

              {/* Additional Information */}
              {(formData.priority !== "normal" ||
                formData.emergency ||
                formData.remarks) && (
                  <div className="card">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-primary-500" />
                      Additional Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      {formData.priority !== "normal" && (
                        <div>
                          <span className="font-medium">Priority:</span>{" "}
                          {formData.priority}
                        </div>
                      )}
                      {formData.emergency && (
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-red-600 font-medium">
                            Emergency Case
                          </span>
                        </div>
                      )}
                      {formData.remarks && (
                        <div>
                          <span className="font-medium">Remarks:</span>{" "}
                          {formData.remarks}
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Validation Warnings for Super Admin */}
            {isSuperAdmin && showAdmissionDateOverride && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800">Admin Override Active</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Date validation restrictions have been overridden for this application.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-700">{errors.submit}</p>
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Terms & Conditions
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    By submitting this application, you confirm that all
                    information provided is accurate and complete. The
                    application will be processed according to the insurance
                    policy terms and hospital procedures.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 7: // Upload Documents (consolidated)
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">Upload Documents</h4>
              <p className="text-sm text-primary-600">Add all required files in one place</p>
            </div>

            {/* Missing items checklist */}
            {(() => {
              const missing = getMissingRequiredDocs();
              return missing.length > 0 ? (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-800 font-medium">Missing required documents</p>
                      <ul className="mt-2 list-disc list-inside text-sm text-amber-800 space-y-1">
                        {missing.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-800">All required documents are added</span>
                  </div>
                </div>
              );
            })()}

            {/* Patient KYC */}
            <div className="grid md:grid-cols-2 gap-6">
              {renderDocumentUpload("patientAadharFrontDoc", "Patient Aadhar - Front", true)}
              {renderDocumentUpload("patientAadharBackDoc", "Patient Aadhar - Back", true)}
              {renderDocumentUpload("patientPanDoc", "Patient PAN Card", formData.isSelf)}
              {renderDocumentUpload("patientAddressProofDoc", "Patient Address Proof", false)}
            </div>

            {/* Policy Documents */}
            <div className="grid md:grid-cols-2 gap-6">
              {formData.isSelf ? (
                <>
                  {renderDocumentUpload(
                    "patientChequeOrStatementDoc",
                    "Cancelled Cheque / Bank Statement",
                    true
                  )}

                  <div className="space-y-3">
                    <label className="form-label">
                      Policy Copy <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 transition-colors ${dragOver["patientPolicyCopyDocs"] ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
                      onDragOver={(e) => handleDragOverGeneric(e, "patientPolicyCopyDocs")}
                      onDragLeave={(e) => handleDragLeaveGeneric(e, "patientPolicyCopyDocs")}
                      onDrop={(e) => handleDropMultiple(e, "patientPolicyCopyDocs", handlePatientPolicyCopyUpload)}
                    >
                      <div className="text-center">
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-2">Drag & drop policy copies here, or click to browse (multiple files allowed)</p>
                        <p className="text-xs text-gray-500 mb-3">PDF, JPEG, PNG up to 5MB each</p>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                          onChange={(e) => handlePatientPolicyCopyUpload(e.target.files)}
                          className="hidden"
                          id="patientPolicyCopyDocs"
                        />
                        <label htmlFor="patientPolicyCopyDocs" className="btn-primary cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" /> Choose Files
                        </label>
                      </div>
                    </div>
                    {errors.patientPolicyCopyDocs && (
                      <p className="form-error">{errors.patientPolicyCopyDocs}</p>
                    )}
                    {(documents.patientPolicyCopyDocs || []).length > 0 && (
                      <div className="space-y-2">
                        {(documents.patientPolicyCopyDocs || []).map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-800">{file.name}</span>
                              <span className="text-xs text-green-600">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button onClick={() => openPreview(file)} className="text-primary-600 hover:text-primary-800 text-sm flex items-center">
                                <Eye className="h-4 w-4 mr-1" /> Preview
                              </button>
                              <button onClick={() => removePatientPolicyCopyDoc(index)} className="text-red-500 hover:text-red-700" title="Remove">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {renderDocumentUpload(
                    "policyHolderAadharFrontDoc",
                    "Policyholder Aadhar - Front",
                    true
                  )}
                  {renderDocumentUpload(
                    "policyHolderAadharBackDoc",
                    "Policyholder Aadhar - Back",
                    true
                  )}
                  {renderDocumentUpload(
                    "policyHolderPanDoc",
                    "Policyholder PAN Card",
                    true
                  )}
                  {renderDocumentUpload(
                    "policyHolderChequeOrStatementDoc",
                    "Policyholder Cheque / Statement",
                    true
                  )}

                  <div className="space-y-3">
                    <label className="form-label">
                      Policyholder Policy Copy <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 transition-colors ${dragOver["policyHolderPolicyCopyDocs"] ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
                      onDragOver={(e) => handleDragOverGeneric(e, "policyHolderPolicyCopyDocs")}
                      onDragLeave={(e) => handleDragLeaveGeneric(e, "policyHolderPolicyCopyDocs")}
                      onDrop={(e) => handleDropMultiple(e, "policyHolderPolicyCopyDocs", handlePolicyHolderPolicyCopyUpload)}
                    >
                      <div className="text-center">
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-2">Drag & drop policyholder policy copies here, or click to browse (multiple files allowed)</p>
                        <p className="text-xs text-gray-500 mb-3">PDF, JPEG, PNG up to 5MB each</p>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                          onChange={(e) => handlePolicyHolderPolicyCopyUpload(e.target.files)}
                          className="hidden"
                          id="policyHolderPolicyCopyDocs"
                        />
                        <label htmlFor="policyHolderPolicyCopyDocs" className="btn-primary cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" /> Choose Files
                        </label>
                      </div>
                    </div>
                    {errors.policyHolderPolicyCopyDocs && (
                      <p className="form-error">{errors.policyHolderPolicyCopyDocs}</p>
                    )}
                    {(documents.policyHolderPolicyCopyDocs || []).length > 0 && (
                      <div className="space-y-2">
                        {(documents.policyHolderPolicyCopyDocs || []).map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-800">{file.name}</span>
                              <span className="text-xs text-green-600">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button onClick={() => openPreview(file)} className="text-primary-600 hover:text-primary-800 text-sm flex items-center">
                                <Eye className="h-4 w-4 mr-1" /> Preview
                              </button>
                              <button onClick={() => removePolicyHolderPolicyCopyDoc(index)} className="text-red-500 hover:text-red-700" title="Remove">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Medical History (optional) */}
            {formData.hasMedicalHistory && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h5 className="font-medium text-gray-800 mb-2">Medical History Documents (Optional)</h5>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 transition-colors ${dragOver["medicalHistoryDocs"] ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
                  onDragOver={(e) => handleDragOverGeneric(e, "medicalHistoryDocs")}
                  onDragLeave={(e) => handleDragLeaveGeneric(e, "medicalHistoryDocs")}
                  onDrop={(e) => handleDropMultiple(e, "medicalHistoryDocs", handleMedicalHistoryDocUpload)}
                >
                  <div className="text-center">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-2">Drag & drop medical history documents here, or click to browse</p>
                    <p className="text-xs text-gray-500 mb-3">PDF, JPEG, PNG up to 100MB each. Multiple files allowed.</p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={(e) => handleMedicalHistoryDocUpload(e.target.files)}
                      className="hidden"
                      id="medicalHistoryDocs"
                    />
                    <label htmlFor="medicalHistoryDocs" className="btn-primary cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" /> Choose Files
                    </label>
                  </div>
                </div>
                {errors.medicalHistoryDocs && <p className="form-error mt-2">{errors.medicalHistoryDocs}</p>}
                {documents.medicalHistoryDocs.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {documents.medicalHistoryDocs.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-800">{file.name}</span>
                          <span className="text-xs text-green-600">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => openPreview(file)} className="text-primary-600 hover:text-primary-800 text-sm flex items-center">
                            <Eye className="h-4 w-4 mr-1" /> Preview
                          </button>
                          <button onClick={() => removeMedicalHistoryDoc(index)} className="text-red-500 hover:text-red-700" title="Remove">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Medical Documents (mandatory) */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-1">Medical Documents <span className="text-red-500">*</span></h5>
              <p className="text-sm text-gray-600 mb-4">Upload prescriptions, lab reports, imaging, etc. Minimum 1 file required.</p>
              <div
                className={`border-2 border-dashed rounded-lg p-6 transition-colors ${dragOver["medicalDocs"] ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
                onDragOver={(e) => handleDragOverGeneric(e, "medicalDocs")}
                onDragLeave={(e) => handleDragLeaveGeneric(e, "medicalDocs")}
                onDrop={(e) => handleDropMultiple(e, "medicalDocs", handleMedicalDocsUpload)}
              >
                <div className="text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Drag & drop medical documents here, or click to browse</p>
                  <p className="text-xs text-gray-500 mb-3">PDF, JPEG, PNG up to 100MB each. Multiple files allowed.</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    onChange={(e) => handleMedicalDocsUpload(e.target.files)}
                    className="hidden"
                    id="medicalDocs"
                  />
                  <label htmlFor="medicalDocs" className="btn-primary cursor-pointer">
                    <Upload className="h-4 w-4 mr-2" /> Choose Files
                  </label>
                </div>
              </div>
              {errors.medicalDocs && <p className="form-error mt-2">{errors.medicalDocs}</p>}
              {documents.medicalDocs.length > 0 && (
                <div className="mt-4 space-y-2">
                  {documents.medicalDocs.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-800">{file.name}</span>
                        <span className="text-xs text-green-600">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => openPreview(file)} className="text-primary-600 hover:text-primary-800 text-sm flex items-center">
                          <Eye className="h-4 w-4 mr-1" /> Preview
                        </button>
                        <button onClick={() => removeMedicalDoc(index)} className="text-red-500 hover:text-red-700" title="Remove">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Investigation Reports (optional) */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-1">Investigation Reports (Optional)</h5>
              <p className="text-sm text-gray-600 mb-4">Upload lab investigations, imaging reports, or diagnostic summaries. Multiple files allowed.</p>
              <div
                className={`border-2 border-dashed rounded-lg p-6 transition-colors ${dragOver["investigationReports"] ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
                onDragOver={(e) => handleDragOverGeneric(e, "investigationReports")}
                onDragLeave={(e) => handleDragLeaveGeneric(e, "investigationReports")}
                onDrop={(e) => handleDropMultiple(e, "investigationReports", handleInvestigationReportsUpload)}
              >
                <div className="text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Drag & drop investigation reports here, or click to browse</p>
                  <p className="text-xs text-gray-500 mb-3">PDF, JPEG, PNG up to 100MB each. Multiple files allowed.</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    onChange={(e) => handleInvestigationReportsUpload(e.target.files)}
                    className="hidden"
                    id="investigationReports"
                  />
                  <label htmlFor="investigationReports" className="btn-primary cursor-pointer">
                    <Upload className="h-4 w-4 mr-2" /> Choose Files
                  </label>
                </div>
              </div>
              {errors.investigationReports && <p className="form-error mt-2">{errors.investigationReports}</p>}
              {(documents.investigationReports || []).length > 0 && (
                <div className="mt-4 space-y-2">
                  {(documents.investigationReports || []).map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-800">{file.name}</span>
                        <span className="text-xs text-green-600">{((file.size || 0) / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => openPreview(file)} className="text-primary-600 hover:text-primary-800 text-sm flex items-center">
                          <Eye className="h-4 w-4 mr-1" /> Preview
                        </button>
                        <button onClick={() => removeInvestigationReport(index)} className="text-red-500 hover:text-red-700" title="Remove">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Document Preview Modal */}
      {preview.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800 truncate pr-4">
                {preview.name || "Document preview"}
              </h3>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close preview"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="bg-white rounded border overflow-auto max-h-[70vh]">
                {(() => {
                  const type = (preview.type || "").toLowerCase();
                  const name = preview.name || "";
                  const isImage = type.includes("image") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
                  const isPdf = type.includes("pdf") || /\.pdf$/i.test(name);
                  if (isImage) {
                    return (
                      <img
                        src={preview.url}
                        alt={name}
                        className="max-h-[70vh] w-auto mx-auto object-contain"
                      />
                    );
                  }
                  if (isPdf) {
                    return (
                      <iframe
                        title={name}
                        src={preview.url}
                        className="w-full h-[70vh]"
                      />
                    );
                  }
                  return (
                    <div className="p-6 text-center">
                      <p className="text-sm text-gray-700 mb-4">
                        Preview not available for this file type.
                      </p>
                      <a
                        href={preview.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center"
                      >
                        Open in new tab
                      </a>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Lead Selection Modal */}
      {showLeadSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Select Lead Generator
              </h3>
              <button
                onClick={() => setShowLeadSelection(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start space-x-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-800">
                      Lead Generator Assignment Required
                    </h4>
                    <p className="text-sm text-blue-700 mt-1">
                      This application will be assigned to the lead generator who onboarded your hospital.
                      This ensures proper tracking and coordination for the application process.
                    </p>
                  </div>
                </div>
              </div>

              {loadingLeadGenerators ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 text-primary-500 mx-auto mb-2 animate-spin" />
                  <p className="text-gray-600">Loading lead generators...</p>
                </div>
              ) : leadGenerators.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-gray-600">No assigned lead generator found.</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Contact admin to ensure your hospital has an assigned lead generator.
                  </p>
                  <button
                    onClick={fetchLeadGenerators}
                    className="mt-3 btn-outline text-sm"
                    disabled={loadingLeadGenerators}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Your Assigned Lead Generator:
                  </label>
                  {leadGenerators.map((leadGen) => (
                    <div
                      key={leadGen.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedLeadGenerator?.id === leadGen.id
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:border-gray-300"
                        }`}
                      onClick={() => handleLeadGeneratorSelection(leadGen)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="leadGenerator"
                              checked={selectedLeadGenerator?.id === leadGen.id}
                              onChange={() => handleLeadGeneratorSelection(leadGen)}
                              className="text-primary-500 focus:ring-primary-500"
                            />
                            <h4 className="font-semibold text-gray-800">
                              {leadGen.entityName}
                              {leadGenerators.length === 1 && (
                                <span className="ml-2 inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                  Assigned
                                </span>
                              )}
                            </h4>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Location:</span>
                              <span className="ml-1 text-gray-700">
                                {leadGen.city}, {leadGen.state}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">CIN:</span>
                              <span className="ml-1 text-gray-700 font-mono text-xs">
                                {leadGen.cinNumber}
                              </span>
                            </div>
                          </div>
                          {leadGen.servicesRequired && leadGen.servicesRequired.length > 0 && (
                            <div className="mt-2">
                              <span className="text-gray-500 text-sm">Services:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {leadGen.servicesRequired.map((service, index) => (
                                  <span
                                    key={index}
                                    className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                                  >
                                    {service}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {errors.leadGenerator && (
                <p className="text-red-500 text-sm">{errors.leadGenerator}</p>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowLeadSelection(false)}
                  className="btn-outline"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleActualSubmit}
                  disabled={!selectedLeadGenerator || loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    "Submit Application"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            New Patient Application
          </h1>
          <p className="text-gray-600">
            Create a comprehensive insurance application for patient treatment
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear the saved draft and start fresh?')) {
                  localStorage.removeItem('newPatientApplication');
                  window.location.reload();
                }
              }}
              className="btn-outline text-sm text-red-600 border-red-300 hover:bg-red-50"
            >
              Clear Draft
            </button>
          )}
          <button onClick={onBack || (() => goBackToDashboard(navigate))} className="btn-outline">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Auto-save Indicator */}
      {lastSaved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-800">
              Auto-saved at {lastSaved.toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      {!submitSuccess && (
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div key={step.id} className="flex items-center">
                  {/* Step Circle */}
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-16 h-16 rounded-full border-2 transition-all duration-300 ${isActive
                        ? "bg-teal-500 border-teal-500 text-white shadow-lg"
                        : isCompleted
                          ? "bg-green-500 border-green-500 text-white"
                          : "bg-gray-50 border-gray-200 text-gray-400"
                        }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="h-7 w-7" />
                      ) : (
                        <Icon className="h-7 w-7" />
                      )}
                    </div>

                    {/* Step Label */}
                    <div className="mt-3 text-center max-w-24">
                      <p
                        className={`text-sm font-medium leading-tight ${isActive
                          ? "text-teal-600"
                          : isCompleted
                            ? "text-green-600"
                            : "text-gray-500"
                          }`}
                      >
                        {step.title}
                      </p>
                      <p
                        className={`text-xs mt-1 ${isActive
                          ? "text-teal-500"
                          : isCompleted
                            ? "text-green-500"
                            : "text-gray-400"
                          }`}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 w-16 mx-4 transition-colors duration-300 ${isCompleted ? "bg-green-500" : "bg-gray-200"
                        }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="card-medical">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      {!submitSuccess && (
        <div className="flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${currentStep === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              }`}
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Previous</span>
          </button>

          {/* Step indicator */}
          <div className="text-sm text-gray-500 hidden md:block">
            Step {currentStep} of {steps.length}
          </div>

          {currentStep < steps.length ? (
            <button
              onClick={nextStep}
              className="flex items-center space-x-2 btn-primary hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              <span>Next</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || getMissingRequiredDocs().length > 0}
              className="flex items-center space-x-2 btn-success disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  <span>Submit Application</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 text-center">
        <div className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Need Help?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Contact our support team if you need assistance with the application
            process
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
            <a
              href="mailto:support@samriddhihealthcare.com"
              className="flex items-center text-teal-600 hover:text-teal-700 transition-colors"
            >
              <Mail className="h-4 w-4 mr-2" />
              support@samriddhihealthcare.com
            </a>
            <a
              href="tel:+91-1800-XXX-XXXX"
              className="flex items-center text-teal-600 hover:text-teal-700 transition-colors"
            >
              <Phone className="h-4 w-4 mr-2" />
              +91 1800-XXX-XXXX
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPatientApplication;